import React from 'react';
import { MapIcon, ShieldCheckIcon, FileTextIcon, CreditCardIcon, SearchIcon, GavelIcon, GlobeIcon, ChevronRightIcon, CalendarIcon, ArrowRightIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
export function HomePage({
  navigateTo
}: {
  navigateTo: (page: string) => void;
}) {
  const {
    language,
    toggleLanguage,
    t
  } = useLanguage();
  const quickServices = [{
    id: 'mouzaMap',
    icon: MapIcon,
    titleBn: 'মৌজা মানচিত্র',
    titleEn: 'Mouza Map',
    action: () => navigateTo('mouzaMap'),
    clickable: true
  }, {
    id: 'cloVerify',
    icon: ShieldCheckIcon,
    titleBn: 'সিএলও যাচাই',
    titleEn: 'CLO Verification',
    action: () => navigateTo('cloVerification'),
    clickable: true
  }, {
    id: 'mutation',
    icon: FileTextIcon,
    titleBn: 'নামজারি আবেদন',
    titleEn: 'Mutation Application',
    action: undefined,
    clickable: false
  }, {
    id: 'tax',
    icon: CreditCardIcon,
    titleBn: 'এলডিট্যাক্স',
    titleEn: 'LDTAX',
    action: undefined,
    clickable: false
  }, {
    id: 'khatianSearch',
    icon: SearchIcon,
    titleBn: 'খতিয়ান অনুসন্ধান',
    titleEn: 'Khatian Search',
    action: undefined,
    clickable: false
  }, {
    id: 'landCase',
    icon: GavelIcon,
    titleBn: 'ভূমি মামলা',
    titleEn: 'Land Case',
    action: undefined,
    clickable: false
  }];
  const stats = [{
    value: '12,450+',
    label: t('totalPlotsDigitized')
  }, {
    value: '8,320+',
    label: t('closIssued')
  }, {
    value: '70 M+',
    label: t('totalKhatian')
  }, {
    value: '64',
    label: t('districtsCovered')
  }];
  const notices = [{
    id: 1,
    date: '15 Mar 2026',
    text: t('notice1')
  }, {
    id: 2,
    date: '10 Mar 2026',
    text: t('notice2')
  }, {
    id: 3,
    date: '05 Mar 2026',
    text: t('notice3')
  }];
  return <div className="min-h-screen bg-[#F8FAFB] flex flex-col font-sans" data-id="element-356">
      {/* Top Government Header Bar */}
      <div className="bg-[#006A4E] text-white py-2 px-4 md:px-8 flex justify-between items-center border-b-4 border-[#DC2626]" data-id="element-357">
        <div className="flex items-center space-x-3" data-id="element-358">
          {/* Government Emblem */}
          <img src="https://cdn.magicpatterns.com/uploads/iWSbHRFjNdqxbSvrrHtxib/bdlogo.png" alt="Bangladesh Government Logo" className="w-10 h-10 rounded-full object-contain bg-white border-2 border-[#C9A84C]" data-id="element-359" />
          <div className="flex flex-col" data-id="element-360">
            <span className="text-sm md:text-base font-bold leading-tight" data-id="element-361">
              {t('govName')}
            </span>
            <span className="text-xs md:text-sm text-green-100 leading-tight" data-id="element-362">
              {t('ministryName')}
            </span>
          </div>
        </div>
        <button onClick={toggleLanguage} className="flex items-center space-x-1 text-sm font-medium hover:text-green-200 transition-colors" data-id="element-363">
          <GlobeIcon className="w-4 h-4" data-id="element-364" />
          <span data-id="element-365">{language === 'bn' ? 'English' : 'বাংলা'}</span>
        </button>
      </div>

      {/* Secondary Navigation Bar */}
      <nav className="bg-white shadow-sm sticky top-0 z-50" data-id="element-366">
        <div className="max-w-7xl mx-auto px-4 md:px-8" data-id="element-367">
          <div className="flex justify-between items-center h-16" data-id="element-368">
            <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigateTo('home')} data-id="element-369">
              <div className="w-8 h-8 bg-[#006A4E] rounded flex items-center justify-center" data-id="element-370">
                <MapIcon className="w-5 h-5 text-white" data-id="element-371" />
              </div>
              <span className="text-xl font-bold text-[#006A4E] hidden md:block" data-id="element-372">
                {t('appTitleShort')}
              </span>
            </div>

            <div className="hidden md:flex space-x-8" data-id="element-373">
              <button onClick={() => navigateTo('home')} className="text-[#006A4E] font-semibold border-b-2 border-[#006A4E] py-5" data-id="element-374">
                {t('publicHome')}
              </button>
              <button onClick={() => navigateTo('mouzaMap')} className="text-gray-600 hover:text-[#006A4E] font-medium py-5 transition-colors" data-id="element-375">
                {t('mouzaMap')}
              </button>
              <button onClick={() => navigateTo('cloVerification')} className="text-gray-600 hover:text-[#006A4E] font-medium py-5 transition-colors" data-id="element-376">
                {t('verifyCLO')}
              </button>
            </div>

            <button onClick={() => navigateTo('login')} className="px-5 py-2 bg-[#006A4E] text-white rounded font-medium hover:bg-[#004D3A] transition-colors shadow-sm text-sm" data-id="element-377">
              {t('login')}
            </button>
          </div>
        </div>
      </nav>

      <main className="flex-1" data-id="element-378">
        {/* Hero Section */}
        <div className="bg-[#006A4E] relative overflow-hidden" data-id="element-379">
          <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'radial-gradient(white 1px, transparent 1px)',
          backgroundSize: '30px 30px'
        }} data-id="element-380"></div>

          <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24 relative z-10 flex flex-col items-center text-center" data-id="element-381">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight max-w-4xl" data-id="element-382">
              {t('appTitle')}
            </h1>
            <p className="text-lg md:text-xl text-green-100 mb-10 max-w-2xl" data-id="element-383">
              {t('heroSubtitle')}
            </p>

            {/* Search Bar - non-navigating */}
            <div className="w-full max-w-3xl bg-white p-2 rounded-lg shadow-lg flex flex-col md:flex-row gap-2" data-id="element-384">
              <div className="flex-1 relative" data-id="element-385">
                <SearchIcon className="w-5 h-5 absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-386" />
                <input type="text" placeholder={t('searchPlaceholder')} className="w-full pl-12 pr-4 py-3 md:py-4 bg-transparent border-none focus:ring-0 text-gray-900 placeholder-gray-500 text-lg outline-none" data-id="element-387" />
              </div>
              <div className="bg-[#C9A84C] text-[#004D3A] px-8 py-3 md:py-4 rounded-md font-bold text-lg flex items-center justify-center opacity-60 cursor-not-allowed select-none" data-id="element-388">
                {t('searchButton')}
              </div>
            </div>
          </div>
        </div>

        {/* Statistics Section */}
        <div className="bg-white border-b border-gray-200" data-id="element-389">
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-8" data-id="element-390">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center divide-x divide-gray-100" data-id="element-391">
              {stats.map((stat, idx) => <div key={idx} className="px-4" data-id="element-392">
                  <div className="text-3xl md:text-4xl font-bold text-[#006A4E] mb-2" data-id="element-393">
                    {stat.value}
                  </div>
                  <div className="text-sm md:text-base text-gray-600 font-medium" data-id="element-394">
                    {stat.label}
                  </div>
                </div>)}
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-395">
          {/* Quick Services Grid */}
          <div className="lg:col-span-2" data-id="element-396">
            <div className="flex items-center justify-between mb-6" data-id="element-397">
              <h2 className="text-2xl font-bold text-gray-900 border-l-4 border-[#006A4E] pl-3" data-id="element-398">
                {t('quickServices')}
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-399">
              {quickServices.map(service => {
              if (service.clickable) {
                return <button key={service.id} onClick={service.action} className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm hover:shadow-md hover:border-[#006A4E] transition-all flex items-center group text-left" data-id="element-400">
                      <div className="w-14 h-14 bg-green-50 rounded-lg flex items-center justify-center mr-4 group-hover:bg-[#006A4E] transition-colors flex-shrink-0" data-id="element-401">
                        <service.icon className="w-7 h-7 text-[#006A4E] group-hover:text-white transition-colors" data-id="element-402" />
                      </div>
                      <div className="flex-1" data-id="element-403">
                        <h3 className="text-lg font-bold text-gray-900 group-hover:text-[#006A4E] transition-colors" data-id="element-404">
                          {language === 'bn' ? service.titleBn : service.titleEn}
                        </h3>
                        <p className="text-sm text-gray-500" data-id="element-405">
                          {language === 'bn' ? service.titleEn : service.titleBn}
                        </p>
                      </div>
                      <ChevronRightIcon className="w-5 h-5 text-gray-300 group-hover:text-[#006A4E] transform group-hover:translate-x-1 transition-all" data-id="element-406" />
                    </button>;
              }
              return <div key={service.id} className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm flex items-center text-left opacity-75" data-id="element-407">
                    <div className="w-14 h-14 bg-gray-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0" data-id="element-408">
                      <service.icon className="w-7 h-7 text-gray-400" data-id="element-409" />
                    </div>
                    <div className="flex-1" data-id="element-410">
                      <h3 className="text-lg font-bold text-gray-700" data-id="element-411">
                        {language === 'bn' ? service.titleBn : service.titleEn}
                      </h3>
                      <p className="text-sm text-gray-400" data-id="element-412">
                        {language === 'bn' ? service.titleEn : service.titleBn}
                      </p>
                    </div>
                    <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded font-medium" data-id="element-413">
                      {language === 'bn' ? 'শীঘ্রই আসছে' : 'Coming Soon'}
                    </span>
                  </div>;
            })}
            </div>
          </div>

          {/* Recent Notices */}
          <div data-id="element-414">
            <div className="flex items-center justify-between mb-6" data-id="element-415">
              <h2 className="text-2xl font-bold text-gray-900 border-l-4 border-[#C9A84C] pl-3" data-id="element-416">
                {t('recentNotices')}
              </h2>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-417">
              <div className="divide-y divide-gray-100" data-id="element-418">
                {notices.map(notice => <div key={notice.id} className="p-5" data-id="element-419">
                    <div className="flex items-center text-sm text-gray-500 mb-2" data-id="element-420">
                      <CalendarIcon className="w-4 h-4 mr-2 text-[#006A4E]" data-id="element-421" />
                      {notice.date}
                    </div>
                    <p className="text-gray-800 font-medium line-clamp-2" data-id="element-422">
                      {notice.text}
                    </p>
                  </div>)}
              </div>
              <div className="w-full p-4 text-sm font-bold text-[#006A4E] bg-gray-50 flex items-center justify-center" data-id="element-423">
                {language === 'bn' ? 'সকল বিজ্ঞপ্তি দেখুন' : 'View All Notices'}{' '}
                <ArrowRightIcon className="w-4 h-4 ml-2" data-id="element-424" />
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#004D3A] text-white pt-12 pb-6 border-t-4 border-[#C9A84C]" data-id="element-425">
        <div className="max-w-7xl mx-auto px-4 md:px-8" data-id="element-426">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8" data-id="element-427">
            <div data-id="element-428">
              <div className="flex items-center space-x-3 mb-4" data-id="element-429">
                {/* Government Emblem */}
                <img src="https://cdn.magicpatterns.com/uploads/iWSbHRFjNdqxbSvrrHtxib/bdlogo.png" alt="Bangladesh Government Logo" className="w-14 h-14 rounded-full object-contain bg-white border-2 border-[#C9A84C] flex-shrink-0" data-id="element-430" />
                <div data-id="element-431">
                  <h3 className="font-bold text-lg" data-id="element-432">{t('ministryName')}</h3>
                  <p className="text-green-200 text-sm" data-id="element-433">{t('govName')}</p>
                </div>
              </div>
              <p className="text-green-100 text-sm leading-relaxed max-w-xs" data-id="element-434">
                {t('heroSubtitle')}
              </p>
            </div>
            <div data-id="element-435">
              <h4 className="font-bold text-lg mb-4 text-[#C9A84C]" data-id="element-436">
                {t('usefulLinks')}
              </h4>
              <ul className="space-y-2 text-green-100 text-sm" data-id="element-437">
                <li data-id="element-438">ভূমি মন্ত্রণালয় পোর্টাল</li>
                <li data-id="element-439">ই-নামজারি</li>
                <li data-id="element-440">ভূমি উন্নয়ন কর</li>
                <li data-id="element-441">স্মার্ট ভূমি রেকর্ড</li>
              </ul>
            </div>
            <div data-id="element-442">
              <h4 className="font-bold text-lg mb-4 text-[#C9A84C]" data-id="element-443">
                {t('contactInfo')}
              </h4>
              <ul className="space-y-2 text-green-100 text-sm" data-id="element-444">
                <li data-id="element-445">বাংলাদেশ সচিবালয়, ঢাকা-১০০০</li>
                <li data-id="element-446">হেল্পলাইন: ১৬২২২</li>
                <li data-id="element-447">ইমেইল: info@minland.gov.bd</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-green-800 pt-6 text-center text-sm text-green-200" data-id="element-448">
            {t('copyright')}
          </div>
        </div>
      </footer>
    </div>;
}